import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import { formatToDDMMYYYY } from "../../utils/formatDateUtils";
import { BankDetails } from "../../models/epcCustomer.model";
import { numberToWordsINR } from "../../utils/commissionPDFUtils";

const styles = StyleSheet.create({
  page: {
    padding: 30,
  },

  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 6,
    textAlign: "center",
  },
  headerDetails: {
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
    textAlign: "center",
  },

  headerText: {
    fontSize: 10,
    marginTop: 4,
    textAlign: "left",
  },

  headerLabelBold: {
    fontWeight: "bold",
  },
  headerLabel: {
    fontSize: 10,
  },
  headerValue: {
    fontSize: 12,
    fontWeight: "bold",
  },

  headerMeta: {
    marginTop: 8,
    fontSize: 10,
    textAlign: "left",
  },

  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 4,
  },

  commissionTable: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: "#000",
  },

  commissionHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderColor: "#000",
  },

  commissionRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#ddd",
  },

  colSr: {
    width: "7%",
    padding: 6,
    fontSize: 10,
    marginRight: 1,
    textAlign: "center",
    borderRightWidth: 1,
  },
  colConfirmAt: {
    width: "14%",
    padding: 6,
    fontSize: 9,
    textAlign: "center",
    borderRightWidth: 1,
  },

  colPlantSize: {
    width: "10%",
    padding: 6,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
  },

  colAmount: {
    width: "10%",
    padding: 6,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
  },

  colNameWithDeduction: {
    width: "30%",
    padding: 6,
    fontSize: 10,
    borderRightWidth: 1,
  },

  // ====== WITHOUT DEDUCTION ======
  colNameNoDeduction: {
    width: "40%",
    padding: 6,
    fontSize: 10,
    borderRightWidth: 1,
  },

  colCalculatedNoDeduction: {
    width: "20%",
    padding: 6,
    fontSize: 10,
    textAlign: "center",
  },
  totalLabelCell: {
    padding: 6,
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "right",
    borderRightWidth: 1,
  },
  totalAmountCell: {
    width: "20%",
    padding: 6,
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
  },
  amountInWordsRow: {
    padding: 6,
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "right",
    borderTopWidth: 1,
  },
  extraCol: {
    width: "10%",
    padding: 6,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
  },
  deductionWide: {
    width: "16%",
    padding: 6,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
  },
  colCalculatedNarrow: {
    width: "15%",
    padding: 6,
    fontSize: 10,
    textAlign: "center",
  },
  adjustmentTable: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#000",
  },

  adjustmentHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderColor: "#000",
  },

  adjustmentRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#ddd",
  },

  adjustmentColSr: {
    width: "10%",
    padding: 6,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
  },

  adjustmentColName: {
    width: "60%",
    padding: 6,
    fontSize: 10,
    borderRightWidth: 1,
  },

  adjustmentColAmount: {
    width: "30%",
    padding: 6,
    fontSize: 10,
    textAlign: "right",
  },
  adjustmentTotalLabel: {
    width: "70%",
    padding: 6,
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "right",
    borderRightWidth: 1,
  },

  adjustmentTotalAmount: {
    width: "30%",
    padding: 6,
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "right",
  },
  adjustmentHeading: {
    marginTop: 20,
    marginBottom: 6,
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "left",
  },
  noteText: {
    marginTop: 6,
    fontSize: 7,
    color: "#555",
    textAlign: "left",
  },
  bankDetailRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  bankDetailLabel: {
    width: "35%",
    fontSize: 10,
    fontWeight: "bold",
  },
  bankDetailValue: {
    width: "65%",
    fontSize: 10,
  },
  bankDetailsWrapper: {
    marginTop: 10,
    marginBottom: 15,
  },

  bankHeadingRow: {
    flexDirection: "row",
    marginBottom: 6,
  },

  bankContentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },

  billTo: {
    width: "50%",
    fontSize: 10,
    lineHeight: 1.4,
  },

  bankDetails: {
    width: "50%",
    fontSize: 10,
    lineHeight: 1.6,
  },
});

interface CommissionItem {
  commissionId: string;
  customerName: string;
  plantSize: string;
  confirmAt: string | undefined;
  commissionAmount: number;
  customCommission?: number;
  calculatedCommission: number;
  status: string;
  excessAmount?: number;
  commissionValue?: number;
  adjustmentAmount?: number;
  materialDeduction?: number;
}

interface CommissionSheetPDFProps {
  commissionItems: CommissionItem[];
  generatedDate: string;
  user?: string;
  salesRepName?: string;
  panNumber?: string;
  bankDetails?: BankDetails;
}

const CommissionSheetPDF: React.FC<CommissionSheetPDFProps> = ({
  commissionItems,
  generatedDate,
  user,
  salesRepName,
  panNumber,
  bankDetails,
}) => {
  const totalCommissionAmount = commissionItems.reduce(
    (sum, item) =>
      sum + (item.calculatedCommission || 0) - (item.materialDeduction || 0),
    0,
  );
  console.log(commissionItems);
  const adjustmentItems = commissionItems.filter(
    (item) => item.adjustmentAmount && item.adjustmentAmount > 0,
  );

  const totalAdjustmentAmount = adjustmentItems.reduce(
    (sum, item) => sum + Number(item.adjustmentAmount || 0),
    0,
  );

  const showDeductionColumn = commissionItems.some(
    (item) => item.customCommission && item.customCommission > 0,
  );
  const showMaterialDeductionColumn = commissionItems.some(
    (item) => item.materialDeduction && item.materialDeduction > 0,
  );

  const showExcessColumn = commissionItems.some(
    (item) => item.excessAmount && item.excessAmount > 0,
  );

  const extraColumnCount =
    (showDeductionColumn ? 1 : 0) + (showExcessColumn ? 1 : 0);

  const descriptionColStyle =
    extraColumnCount > 0
      ? styles.colNameWithDeduction
      : styles.colNameNoDeduction;

  const hasAnyExtra = showDeductionColumn || showExcessColumn;

  const totalColStyle = hasAnyExtra
    ? styles.colCalculatedNarrow
    : styles.colCalculatedNoDeduction;

  const deductionColStyle = hasAnyExtra
    ? styles.deductionWide
    : styles.extraCol;

  const totalAmountWidth = hasAnyExtra ? "15%" : "20%";
  const totalLabelWidth = hasAnyExtra ? "87%" : "80%";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Text style={styles.title}>Commission Sheet</Text>

        {/* Header Details (BORDER BELOW USER) */}
        <View style={styles.headerDetails}>
          <Text style={styles.headerText}>
            <Text style={styles.headerLabel}>Sales Representative: </Text>
            <Text style={styles.headerValue}>{salesRepName}</Text>
          </Text>
          <Text style={styles.headerText}>
            <Text style={styles.headerLabel}>PAN Number: </Text>
            <Text style={styles.headerValue}>{panNumber}</Text>
          </Text>

          <Text style={styles.headerText}>
            <Text style={styles.headerLabel}>Sales Executive: </Text>
            <Text style={styles.headerValue}>{user}</Text>
          </Text>
        </View>

        <View style={styles.headerMeta}>
          <Text style={styles.headerText}>
            <Text style={styles.headerLabelBold}>Date: </Text>
            {generatedDate}
          </Text>
        </View>

        <View style={styles.bankDetailsWrapper}>
          <View style={styles.bankHeadingRow}>
            <View style={styles.billTo}>
              <Text style={styles.sectionTitle}>Bill To :</Text>
            </View>

            <View style={styles.bankDetails}>
              <Text style={styles.sectionTitle}>Bank Details :</Text>
            </View>
          </View>

          <View style={styles.bankContentRow}>
            {/* Bill To */}
            <View style={styles.billTo}>
              <Text>Frelit Energy Pvt. Ltd.</Text>
              <Text>
                B-20, Bhagya Laxmi Society,{"\n"}Beside Aton Gym, Near Darbar
                Chokdi,
              </Text>
              <Text>Manjalpur, Vadodara, Gujarat</Text>
              <Text>390011</Text>
              <Text>079-41056633</Text>
              <Text>info@frelitenergy.com</Text>
              <Text>GST: 24AADCF7701R1ZB</Text>
            </View>

            {/* Bank Details */}
            {bankDetails && (
              <View style={styles.bankDetails}>
                <View style={styles.bankDetailRow}>
                  <Text style={styles.bankDetailLabel}>Account Name:</Text>
                  <Text style={styles.bankDetailValue}>
                    {bankDetails.bankAccountName}
                  </Text>
                </View>

                <View style={styles.bankDetailRow}>
                  <Text style={styles.bankDetailLabel}>Account Number:</Text>
                  <Text style={styles.bankDetailValue}>
                    {bankDetails.bankAccountNumber}
                  </Text>
                </View>

                <View style={styles.bankDetailRow}>
                  <Text style={styles.bankDetailLabel}>IFSC Code:</Text>
                  <Text style={styles.bankDetailValue}>{bankDetails.ifsc}</Text>
                </View>
              </View>
            )}
          </View>
        </View>

        <View style={styles.commissionTable}>
          {/* Table Header */}
          <View style={styles.commissionHeaderRow}>
            <Text style={styles.colSr}>
              {showDeductionColumn ? "Sr.\nNo" : "Sr.No"}
            </Text>
            <Text style={styles.colConfirmAt}>Confirm Date</Text>
            <Text style={descriptionColStyle}>Customer Name</Text>
            <Text style={styles.colPlantSize}>Plant Size</Text>
            <Text style={styles.colAmount}>Rate</Text>
            {showDeductionColumn && (
              <Text style={deductionColStyle} wrap={false}>
                Deduction
              </Text>
            )}
            {showMaterialDeductionColumn && (
              <Text style={styles.extraCol} wrap={false}>
                Material Deduction
              </Text>
            )}
            {showExcessColumn && (
              <Text style={styles.extraCol} wrap={false}>
                Excess
              </Text>
            )}

            <Text style={totalColStyle}>Total</Text>
          </View>
          {/* Table Rows */}
          {commissionItems.map((item, index) => (
            <View key={item.commissionId} style={styles.commissionRow}>
              <Text style={styles.colSr}>{index + 1}</Text>
              <Text style={styles.colConfirmAt}>
                {formatToDDMMYYYY(item.confirmAt)}
              </Text>
              <Text style={descriptionColStyle}>{item.customerName}</Text>
              <Text style={styles.colPlantSize}>{item.plantSize}</Text>
              <Text style={styles.colAmount}>
                {item?.commissionValue?.toLocaleString("en-IN")}
              </Text>
              {showDeductionColumn && (
                <Text style={deductionColStyle}>
                  {item.customCommission?.toLocaleString("en-IN") || "-"}
                </Text>
              )}
              {showMaterialDeductionColumn && (
                <Text style={styles.extraCol}>
                  {item.materialDeduction?.toLocaleString("en-IN") || "-"}
                </Text>
              )}
              {showExcessColumn && (
                <Text style={styles.extraCol}>
                  {item.excessAmount?.toLocaleString("en-IN") || "-"}
                </Text>
              )}
              <Text style={totalColStyle}>
                {(
                  Number(item.calculatedCommission || 0) -
                  Number(item.materialDeduction || 0) +
                  Number(item.adjustmentAmount || 0)
                ).toLocaleString("en-IN")}
              </Text>
            </View>
          ))}
          <View
            style={[
              styles.commissionRow,
              {
                borderTopWidth: 1,
                borderTopColor: "#000",
                backgroundColor: "#fff",
              },
            ]}
          >
            <Text
              style={[
                styles.totalLabelCell,
                {
                  width: totalLabelWidth,
                },
              ]}
            >
              Total
            </Text>

            <Text style={[styles.totalAmountCell, { width: totalAmountWidth }]}>
              {totalCommissionAmount.toLocaleString("en-IN")}
            </Text>
          </View>

          <View>
            <Text style={styles.amountInWordsRow}>
              {numberToWordsINR(totalCommissionAmount)}
            </Text>
          </View>
        </View>

        <View>
          <Text style={styles.noteText}>
            *Excess amount shown is post 8.9% GST deduction
          </Text>
        </View>

        {adjustmentItems.length > 0 && (
          <>
            <Text style={styles.adjustmentHeading}>
              Adjustment / Due Amount
            </Text>

            <View style={styles.adjustmentTable}>
              {/* Header */}
              <View style={styles.adjustmentHeaderRow}>
                <Text style={styles.adjustmentColSr}>Sr No</Text>
                <Text style={styles.adjustmentColName}>Customer Name</Text>
                <Text style={styles.adjustmentColAmount}>Due Amount</Text>
              </View>

              {/* Rows */}
              {adjustmentItems.map((item, index) => (
                <View key={index} style={styles.adjustmentRow}>
                  <Text style={styles.adjustmentColSr}>{index + 1}</Text>
                  <Text style={styles.adjustmentColName}>
                    {item.customerName}
                  </Text>
                  <Text style={styles.adjustmentColAmount}>
                    {item.adjustmentAmount?.toLocaleString("en-IN")}
                  </Text>
                </View>
              ))}
              <View
                style={[
                  styles.adjustmentRow,
                  { borderTopWidth: 1, borderTopColor: "#000" },
                ]}
              >
                <Text style={styles.adjustmentTotalLabel}>Total</Text>
                <Text style={styles.adjustmentTotalAmount}>
                  {totalAdjustmentAmount}
                </Text>
              </View>
            </View>
          </>
        )}
        {/* ===== APPROVAL SIGNATURE ===== */}
        <View style={{ marginTop: 40 }}>
          {/* Empty space for physical signature */}
          <View
            style={{
              height: 50, // signature space
            }}
          />

          <Text
            style={{
              fontSize: 10,
              fontWeight: "bold",
              textAlign: "left",
            }}
          >
            Approved by:
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default CommissionSheetPDF;
